/**
 * TanStack Query hooks for `braze-event-sync-integration`.
 * Wrappers over ./api with `braze-event-sync-integration` query-key invalidation.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	brazeEventSyncIntegrationQueryKeys,
	enqueueEvent,
	list,
	markStatus,
	type BrazeSyncStatus,
	type EnqueueBrazeEventBody,
	type EnqueueBrazeEventResponse,
	type ListBrazeEventsResponse,
	type MarkBrazeStatusBody,
	type MarkBrazeStatusResponse,
} from "./api";

export function useBrazeEventList(
	status?: BrazeSyncStatus,
): UseQueryResult<ListBrazeEventsResponse, Error> {
	return useQuery({
		queryKey: brazeEventSyncIntegrationQueryKeys.list(status),
		queryFn: () => list(status),
	});
}

export function useBrazeEventEnqueue(): UseMutationResult<
	EnqueueBrazeEventResponse,
	Error,
	EnqueueBrazeEventBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: enqueueEvent,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: brazeEventSyncIntegrationQueryKeys.all });
		},
	});
}

export function useBrazeEventMarkStatus(): UseMutationResult<
	MarkBrazeStatusResponse,
	Error,
	{ eventId: string; body: MarkBrazeStatusBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ eventId, body }) => markStatus(eventId, body),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: brazeEventSyncIntegrationQueryKeys.all });
		},
	});
}
